import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Download,
  FileText,
  Bell,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  RefreshCw,
  Loader2,
  AlertCircle,
  Video,
  X,
  Link,
  CheckCircle,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const PAGE_SIZE = 10;

// Update this list to match ProgrammeCategories.jsx
const ALL_PROGRAMS = [
  'Advance Growth Mastery',
  'Creative Manifestation Practice',
  'Limitless Mind Rewiring Programme',
  'Career Dream Mastery',
  'Growth Mastery',
  'Special Growth Mastery',
  'Elite Growth Mastery',
  'Legacy Growth Mastery (12 Months)',
  'High-Performance Leadership',
  'Advance Leadership Transformation Mastery'
];

const AdminBooking = ({ filterType = 'all' }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);

  // Video Link Modal State
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedBookingForLink, setSelectedBookingForLink] = useState(null);
  const [linkInput, setLinkInput] = useState("");
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);

  // Reminder Modal State
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedBookingForReminder, setSelectedBookingForReminder] = useState(null);
  const [reminderSuccess, setReminderSuccess] = useState(false);

  // Reset Password Modal State
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedBookingForReset, setSelectedBookingForReset] = useState(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all'); // all | upcoming | past
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // -------------------- Fetch bookings (useCallback) --------------------
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/adminlogin');
        return;
      }

      const res = await fetch(`${API_BASE}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/adminlogin');
        return;
      }

      if (!res.ok || !data.success) {
        // Fallback for demo if backend is offline or empty
        const stored = localStorage.getItem('exiser_bookings');
        if (stored) {
          setBookings(JSON.parse(stored));
        } else {
          setError(data.message || 'Failed to fetch bookings.');
        }
      } else {
        setBookings(data.bookings || []);
      }
    } catch (err) {
      console.error('Error loading bookings:', err);
      // Fallback for demo
      const stored = localStorage.getItem('exiser_bookings');
      if (stored) {
        setBookings(JSON.parse(stored));
      } else {
        setError('Could not connect to server.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // -------------------- Helpers --------------------
  const isPastBooking = (b) => {
    // Check booking_date (backend) or date (local storage)
    const dateVal = b.booking_date || b.date;
    const timeVal = b.booking_time || b.time || '00:00';

    if (!dateVal) return false;

    const dateStr = dateVal.toString().slice(0, 10);
    const timeStr = timeVal.toString().slice(0, 5);
    const dt = new Date(`${dateStr}T${timeStr}`);

    if (isNaN(dt.getTime())) return false;

    return dt < new Date();
  };

  // Unique session type options
  const sessionOptions = useMemo(() => {
    const set = new Set();

    if (filterType === 'all' || filterType === '1:1') {
      set.add('1 : 1 Session');
    }
    if (filterType === 'all') {
      set.add('1:1 Session'); // Legacy/Alternative
      set.add('Counselling');
    }

    if (filterType === 'program' || filterType === 'all') {
      ALL_PROGRAMS.forEach(p => set.add(p));
    }

    // Add any dynamic types from bookings (filtered by current list view ideally, but checking booking type matches filter helps)
    bookings.forEach((b) => {
      const type = b.session_type || b.type;
      if (!type) return;

      // Check if this type belongs in the current filterType view
      const isTypeOneToOne =
        type === '1 : 1 Session' ||
        type === '1:1 Session' ||
        type === 'Counselling' ||
        type.includes('1:1');

      if (filterType === '1:1') return; // User requested to remove subtypes from dropdown
      if (filterType === 'program' && isTypeOneToOne) return;

      set.add(type);
    });

    return Array.from(set);
  }, [bookings, filterType]);

  // Filtered list
  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const dateVal = b.booking_date || b.date;
      const dateStr = dateVal ? dateVal.toString().slice(0, 10) : '';

      const name = b.name || b.clientName || '';
      const email = b.email || '';

      // Search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !name.toLowerCase().includes(term) &&
          !email.toLowerCase().includes(term) &&
          !b.booking_code?.toLowerCase().includes(term)
        ) {
          return false;
        }
      }

      // Date range filter
      if (dateFrom && (!dateStr || dateStr < dateFrom)) return false;
      if (dateTo && (!dateStr || dateStr > dateTo)) return false;

      // Status filter
      if (statusFilter === 'upcoming' && isPastBooking(b)) return false;
      if (statusFilter === 'past' && !isPastBooking(b)) return false;

      // Session type filter (Dropdown)
      const type = b.session_type || b.type;
      if (sessionFilter !== 'all' && type !== sessionFilter) {
        return false;
      }

      // Check if it's a 1:1 session (has date/time OR matched specific keywords)
      const isOneToOne = (b.date && b.time) ||
        type === 'Counselling' ||
        type === 'one_to_one' ||
        (type && (type.includes('1:1') || type.includes('1 : 1')));

      // FilterType (Prop: 1:1 vs Program)
      if (filterType === '1:1') {
        if (!isOneToOne) return false;
      } else if (filterType === 'program') {
        if (isOneToOne) return false;
      }

      return true;
    });
  }, [bookings, statusFilter, dateFrom, dateTo, sessionFilter, searchTerm, filterType]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const startIndex = (currentPageSafe - 1) * PAGE_SIZE;
  const currentPageItems = filteredBookings.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  // Handlers
  const handleResetFilters = () => {
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSessionFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Reset Password Handlers
  const openResetModal = (booking) => {
    setSelectedBookingForReset(booking);
    setResetSuccess(false);
    setShowResetModal(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedBookingForReset) return;

    setSendingReset(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: selectedBookingForReset.email })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setResetSuccess(true);
        // Auto close
        setTimeout(() => {
          setShowResetModal(false);
          setSendingReset(false);
        }, 2000);
      } else {
        alert(data.message || "Failed to reset password");
        setSendingReset(false);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to reset password");
      setSendingReset(false);
    }
  };

  // Reminder Modal handler
  const openReminderModal = (booking) => {
    setSelectedBookingForReminder(booking);
    setReminderSuccess(false);
    setShowReminderModal(true);
  };

  // Actual API Call triggered from Modal
  const handleConfirmSendReminder = async () => {
    if (!selectedBookingForReminder) return;

    setSendingReminder(selectedBookingForReminder.id);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE}/api/admin/send-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bookingId: selectedBookingForReminder.id }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setReminderSuccess(true);
        // Auto close after 2 seconds
        setTimeout(() => {
          setShowReminderModal(false);
          setSendingReminder(null);
        }, 2000);
      } else {
        alert(data.message || 'Failed to send reminder.');
        setSendingReminder(null);
      }
    } catch (err) {
      console.error(err);
      alert('Error sending reminder.');
      setSendingReminder(null);
    }
  };

  const handleSendMeetingLink = async (e) => {
    e.preventDefault();
    if (!linkInput || !selectedBookingForLink) return;

    setSendingLink(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE}/api/admin/send-meeting-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bookingId: selectedBookingForLink.id, meetingLink: linkInput }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setLinkSuccess(true);
        // Refresh bookings to show updated status/link if we were showing it
        fetchBookings();
      } else {
        alert(data.message || 'Failed to send meeting link.');
      }
    } catch (err) {
      console.error(err);
      alert('Error sending meeting link.');
    } finally {
      setSendingLink(false);
    }
  };

  const openLinkModal = (booking) => {
    setSelectedBookingForLink(booking);
    setLinkInput(booking.video_link || ""); // Pre-fill if exists
    setLinkSuccess(false);
    setShowLinkModal(true);
  };

  // CSV Export
  const handleDownloadCSV = () => {
    if (!filteredBookings.length) return;
    const headers = [
      'ID',
      'Name',
      'Email',
      'Phone',
      'Session',
      'Date',
      'Time',
      'Status',
    ];

    const rows = filteredBookings.map((b) => {
      const dateVal = b.booking_date || b.date;
      const timeVal = b.booking_time || b.time;
      const dateStr = dateVal ? dateVal.toString().slice(0, 10) : '';
      const status = isPastBooking(b) ? 'Completed' : 'Upcoming';

      return [
        b.booking_code || b.id || '',
        b.name || b.clientName || '',
        b.email || '',
        b.phone || '',
        b.session_type || b.type || '',
        dateStr,
        timeVal || '',
        status,
      ];
    });

    const escape = (val) =>
      `"${(val ?? '').toString().replace(/"/g, '""')}"`;
    const csvContent =
      headers.map(escape).join(',') +
      '\n' +
      rows.map((r) => r.map(escape).join(',')).join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `exiser-bookings-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export
  const handleDownloadPDF = () => {
    if (!filteredBookings.length) return;
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor(59, 7, 100);
    doc.text('Exiser Bookings Report', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(
      `Generated on: ${new Date().toLocaleDateString()}`,
      14,
      28
    );

    const tableColumn = [
      'ID',
      'Name',
      'Email',
      'Phone',
      'Session',
      'Date',
      'Time',
      'Status',
    ];
    const tableRows = filteredBookings.map((b) => {
      const dateVal = b.booking_date || b.date;
      const timeVal = b.booking_time || b.time;
      const dateStr = dateVal ? dateVal.toString().slice(0, 10) : '';
      const status = isPastBooking(b) ? 'Completed' : 'Upcoming';

      return [
        b.booking_code || b.id || '',
        b.name || b.clientName || '',
        b.email || '',
        b.phone || '',
        b.session_type || b.type || '',
        dateStr,
        timeVal || '',
        status,
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 7, 100] },
    });

    doc.save(
      `exiser-bookings-${new Date().toISOString().slice(0, 10)}.pdf`
    );
  };

  return (
    <>
      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="tex-lg font-bold text-gray-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-exiser-primary" />
                Send Reminder
              </h3>
              <button
                onClick={() => setShowReminderModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {reminderSuccess ? (
                <div className="flex flex-col items-center justify-center text-center py-6">
                  <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">
                    Reminder Sent!
                  </h4>
                  <p className="text-sm text-gray-500">
                    The reminder email has been sent successfully.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
                    <p>
                      Are you sure you want to send a session reminder to{' '}
                      <span className="font-bold">
                        {selectedBookingForReminder?.email}
                      </span>
                      ?
                    </p>
                    <div className="mt-2 text-xs text-blue-600">
                      Booking ID: {selectedBookingForReminder?.booking_code || selectedBookingForReminder?.id} <br />
                      Session: {selectedBookingForReminder?.session_type}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => setShowReminderModal(false)}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmSendReminder}
                      disabled={sendingReminder === selectedBookingForReminder?.id}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-exiser-primary text-white font-bold hover:bg-purple-900 transition-colors shadow-lg shadow-purple-200 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {sendingReminder === selectedBookingForReminder?.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4" />
                          Send Reminder
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-gray-600" />
                Reset Password
              </h3>
              <button
                onClick={() => setShowResetModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {resetSuccess ? (
                <div className="flex flex-col items-center justify-center text-center py-4">
                  <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-1">
                    Password Reset!
                  </h4>
                  <p className="text-sm text-gray-500">
                    A new password has been emailed to the user.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to reset the password for <strong>{selectedBookingForReset?.email}</strong>?
                  </p>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-800">
                    Warning: This will generate a new random password and email it to the user immediately.
                  </div>

                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => setShowResetModal(false)}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmResetPassword}
                      disabled={sendingReset}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {sendingReset ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Confirm Reset
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden min-h-[600px] flex flex-col">
        {/* Header Panel */}
        <div className="p-6 border-b border-gray-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-gray-50/50">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 hover:text-gray-900 transition-colors shadow-sm"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">
                {filterType === '1:1' && '1:1 Sessions & Counselling'}
                {filterType === 'program' && 'Course Enrollments'}
                {filterType === 'all' && 'All Bookings'}
              </h3>
              <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                {bookings.filter((b) => !isPastBooking(b)).length} upcoming /{' '}
                {bookings.length} total
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={fetchBookings}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-exiser-primary transition-colors text-gray-500"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              />
            </button>
            <div className="h-8 w-px bg-gray-200 mx-1"></div>
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors border border-gray-200 shadow-sm"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors border border-gray-200 shadow-sm"
            >
              <FileText className="w-4 h-4" /> PDF
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="p-4 bg-white border-b border-gray-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, email, ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm bg-gray-50"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-8 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm appearance-none bg-gray-50 cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Completed / Past</option>
            </select>
          </div>

          {/* Session Type */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={sessionFilter}
              onChange={(e) => {
                setSessionFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-8 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm appearance-none bg-gray-50 cursor-pointer"
            >
              <option value="all">All Types</option>
              {sessionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="w-1/2 px-2 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary text-xs bg-gray-50"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="w-1/2 px-2 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary text-xs bg-gray-50"
            />
          </div>
        </div>

        {/* Table Area */}
        <div className="flex-1 overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 border-b border-gray-200">
                  ID/Email & Date
                </th>
                <th className="px-6 py-4 border-b border-gray-200">
                  Client Info
                </th>
                <th className="px-6 py-4 border-b border-gray-200">
                  Session
                </th>
                <th className="px-6 py-4 border-b border-gray-200">
                  Status
                </th>
                <th className="px-6 py-4 border-b border-gray-200 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-20 text-center text-gray-500"
                  >
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-exiser-primary" />
                    Loading bookings data...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-20 text-center text-red-500 bg-red-50"
                  >
                    <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                    {error}
                  </td>
                </tr>
              ) : filteredBookings.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-20 text-center text-gray-500 text-sm"
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                        <Filter className="w-6 h-6 text-gray-300" />
                      </div>
                      <p className="font-bold text-gray-900">
                        No bookings found
                      </p>
                      <p className="text-gray-400 text-xs mb-4">
                        Try adjusting your filters or search terms.
                      </p>
                      <button
                        onClick={handleResetFilters}
                        className="text-exiser-primary font-bold uppercase text-xs hover:underline"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                currentPageItems.map((booking) => {
                  const past = isPastBooking(booking);
                  const dateVal = booking.booking_date || booking.date;
                  const timeVal = booking.booking_time || booking.time;
                  const dateStr = dateVal
                    ? new Date(dateVal).toLocaleDateString()
                    : '-';

                  // Check if it's a 1:1 session (by type string or presence of date/time)
                  const is1to1 = (booking.session_type || booking.type || '').toString().match(/1\s*:\s*1|Counselling/i) || (booking.date && booking.time);

                  return (
                    <tr
                      key={booking.booking_code || booking.id}
                      className="hover:bg-purple-50/20 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="font-mono text-xs font-bold text-exiser-primary bg-purple-50 inline-block px-2 py-0.5 rounded mb-1">
                          {booking.booking_code || booking.id}
                        </div>
                        <div className="text-xs font-bold text-gray-800 mb-1">
                          {booking.email}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {dateStr}
                        </div>
                        <div className="text-xs text-gray-500 ml-4">
                          {timeVal}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-xs font-bold uppercase">
                            {(booking.name ||
                              booking.clientName ||
                              '?'
                            ).charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-sm">
                              {booking.name || booking.clientName}
                            </div>
                            <div className="text-xs text-gray-500 font-sans">
                              {booking.email}
                            </div>
                            <div className="text-xs text-gray-400 font-sans">
                              {booking.phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                          {booking.session_type || booking.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {past ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-500 border border-gray-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>{' '}
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-green-50 text-green-600 border border-green-200 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>{' '}
                            Upcoming
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openResetModal(booking)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wide hover:bg-gray-200 transition-all shadow-sm border border-gray-200"
                            title="Reset Password"
                          >
                            <RefreshCw className="w-3 h-3" /> Reset PW
                          </button>

                          <button
                            onClick={() => openLinkModal(booking)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500 text-white text-[10px] font-bold uppercase tracking-wide hover:bg-green-600 transition-all shadow-sm hover:shadow active:scale-95 border border-green-600"
                            title="Share Meeting Link"
                          >
                            <Video className="w-3 h-3" /> Share Link
                          </button>

                          {/* Reminder - Only for 1:1 Sessions */}
                          {is1to1 && (
                            <button
                              onClick={() => openReminderModal(booking)}
                              disabled={sendingReminder === booking.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-exiser-primary text-white text-[10px] font-bold uppercase tracking-wide hover:bg-purple-900 transition-all shadow-sm hover:shadow active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                              title="Send Reminder Email"
                            >
                              {sendingReminder === booking.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Bell className="w-3 h-3" />
                              )}
                              Reminder
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <span className="text-gray-500">
            Page{' '}
            <span className="font-bold text-gray-900">
              {currentPageSafe}
            </span>{' '}
            of {totalPages} — Showing {currentPageItems.length} records
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setCurrentPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPageSafe === 1}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pNum = i + 1;
                if (totalPages > 5 && currentPageSafe > 3) {
                  pNum = currentPageSafe - 2 + i;
                }
                if (pNum > totalPages) return null;

                return (
                  <button
                    key={pNum}
                    onClick={() => setCurrentPage(pNum)}
                    className={`w-8 h-8 rounded-lg font-bold transition-colors ${currentPageSafe === pNum
                      ? 'bg-exiser-primary text-white shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {pNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() =>
                setCurrentPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={currentPageSafe === totalPages}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Video Link Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative animate-scaleIn">
              <button
                onClick={() => setShowLinkModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 p-1 rounded-full"
              >
                <X size={20} />
              </button>

              <div className="mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4 text-exiser-primary">
                  <Video size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Send Meeting Link</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Share a video conference link with <strong>{selectedBookingForLink?.name}</strong>.
                </p>

                {/* Duplicate Link Warning */}
                {selectedBookingForLink?.video_link && !linkSuccess && (
                  <div className="mt-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs font-medium animate-fadeIn">
                    <AlertCircle className="w-5 h-5 shrink-0 text-amber-600" />
                    <p>
                      <strong>Warning:</strong> A link has already been sent to this user.
                      Sending again will <u>overwrite</u> the existing link and send a new notification.
                    </p>
                  </div>
                )}
              </div>

              {linkSuccess ? (
                <div className="flex flex-col items-center justify-center py-8 animate-scaleIn">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                    <CheckCircle size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Link Sent!</h3>
                  <p className="text-sm text-gray-500 mt-1 mb-6">Meeting details shared successfully.</p>
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="px-6 py-2 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors text-sm"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSendMeetingLink} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1">
                      Meeting URL
                    </label>
                    <div className="relative">
                      <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="url"
                        placeholder="https://zoom.us/j/..."
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-exiser-primary/20 focus:border-exiser-primary transition-all text-sm"
                        value={linkInput}
                        onChange={(e) => setLinkInput(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowLinkModal(false)}
                      className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sendingLink}
                      className="flex-1 py-3 bg-exiser-primary text-white font-bold rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {sendingLink ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>Send Link</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminBooking;